import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUp, MessageSquarePlus, Vote, Lock, CheckCircle2, AlertTriangle,
  ChevronDown, Eye, EyeOff, Sparkles, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PollBuilder } from "../components/PollsAdminPanel";
import { ContentActionsMenu, EditedLabel } from "../components/ContentActionsMenu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUS_LABEL = {
  pending: "Open for upvotes",
  considered: "Being considered",
  launched: "Launched as a poll",
  rejected: "Closed",
};
const STATUS_TONE = {
  pending: "bg-[#F0ECF7] text-[#6B4E8A]",
  considered: "bg-[#FFFBEB] text-[#D97706]",
  launched: "bg-[#8DB5A0]/15 text-[#3F7C5C]",
  rejected: "bg-[#FEE2E2] text-[#B91C1C]",
};

const CHART_COLORS = ["#6B4E8A", "#8DB5A0", "#D89B4E", "#E07A5F", "#5A9BD4", "#A98BC4"];

// ---------------------------------------------------------------------------
// Suggestion sub-components
// ---------------------------------------------------------------------------

const SuggestionCard = ({ s, onUpvote, onEdit, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="bg-white border border-[#E5DFEC] rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
    data-testid={`suggestion-card-${s.suggestion_id}`}
  >
    <div className="flex gap-3 sm:gap-4">
      <button
        type="button"
        onClick={() => onUpvote(s)}
        disabled={s.is_own}
        className={`flex flex-col items-center justify-center w-12 sm:w-14 shrink-0 rounded-xl border transition-colors py-1.5 ${
          s.viewer_upvoted
            ? "border-[#6B4E8A] bg-[#6B4E8A] text-white"
            : s.is_own
            ? "border-[#E5DFEC] bg-[#FAF7F2] text-[#94A3B8] cursor-not-allowed"
            : "border-[#E5DFEC] hover:border-[#6B4E8A] text-[#6B4E8A]"
        }`}
        aria-label={s.viewer_upvoted ? "Remove upvote" : "Upvote"}
        title={s.is_own ? "You can't upvote your own suggestion" : ""}
        data-testid={`upvote-btn-${s.suggestion_id}`}
      >
        <ArrowUp size={18} strokeWidth={2.5} />
        <span className="text-sm font-semibold mt-0.5" data-testid={`upvote-count-${s.suggestion_id}`}>
          {s.upvote_count || 0}
        </span>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold text-[#6B4E8A] leading-snug break-words">{s.title}</h3>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_TONE[s.status] || ""}`}>
              {STATUS_LABEL[s.status] || s.status}
            </span>
            <ContentActionsMenu
                canManage={s.can_manage}
                reportType="poll_suggestion"
                reportId={s.suggestion_id}
                testIdPrefix={`suggestion-${s.suggestion_id}`}
                onEdit={() => onEdit(s)}
                onDelete={() => onDelete(s)}
                deleteTitle="Delete this suggestion?"
                deleteDescription="This permanently removes the suggestion and all of its upvotes. This cannot be undone."
              />
          </div>
        </div>
        {s.description && (
          <p className="text-sm text-[#64748B] mt-1.5 leading-relaxed">{s.description}</p>
        )}
        {s.founder_note && (
          <div className="mt-3 bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg px-3 py-2">
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-medium mb-1 flex items-center gap-1">
              <Sparkles size={12} /> Founder note
            </p>
            <p className="text-xs text-[#6B4E8A]">{s.founder_note}</p>
          </div>
        )}
        <div className="flex items-center justify-between mt-3 text-xs text-[#94A3B8]">
          <span>by {s.suggested_by_name || "a member"} · {new Date(s.created_at).toLocaleDateString("en-CA")}</span>
          <EditedLabel editedAt={s.edited_at} testId={`suggestion-${s.suggestion_id}-edited`} />
        </div>
      </div>
    </div>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Poll voting / results
// ---------------------------------------------------------------------------

const ChartBlock = ({ block }) => {
  const data = block.counts || [];
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="mt-3">
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5DFEC" horizontal={false} />
          <XAxis type="number" allowDecimals={false} domain={[0, max]} stroke="#94A3B8" fontSize={11} />
          <YAxis type="category" dataKey="label" width={120} stroke="#6B4E8A" fontSize={12} />
          <Tooltip />
          <Bar dataKey="value" radius={[0, 8, 8, 0]}>
            {data.map((_, idx) => (
              <Bar key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {block.type === "scale" && block.average !== null && block.average !== undefined && (
        <p className="text-xs text-[#64748B] mt-1">
          Average: <strong className="text-[#6B4E8A]">{block.average}</strong>
        </p>
      )}
    </div>
  );
};

const TextResponses = ({ block }) => (
  <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
    {(block.responses || []).slice(0, 50).map((r, idx) => (
      <div key={idx} className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg px-3 py-2 text-sm text-[#6B4E8A]">
        {r}
      </div>
    ))}
    {(!block.responses || block.responses.length === 0) && (
      <p className="text-xs text-[#94A3B8] italic">No text responses yet.</p>
    )}
  </div>
);

const PollResults = ({ results }) => (
  <div className="space-y-5">
    <p className="text-xs uppercase tracking-wider text-[#94A3B8]">
      {results.total} response{results.total === 1 ? "" : "s"}
    </p>
    {(results.questions || []).map((q) => (
      <div key={q.question_id} className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-4">
        <h4 className="font-medium text-[#6B4E8A] text-sm">{q.text}</h4>
        {q.type === "text" ? <TextResponses block={q} /> : <ChartBlock block={q} />}
      </div>
    ))}
  </div>
);

const PollVotingForm = ({ poll, onSubmit, submitting }) => {
  const [answers, setAnswers] = useState({});
  const [otherText, setOtherText] = useState({}); // per-question "Other" free text

  const setSingle = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const setMulti = (qid, val) => {
    setAnswers((a) => {
      const cur = Array.isArray(a[qid]) ? a[qid] : [];
      const has = cur.includes(val);
      return { ...a, [qid]: has ? cur.filter((v) => v !== val) : [...cur, val] };
    });
  };
  const toggleOtherSingle = (qid) => setSingle(qid, answers[qid] === "__OTHER__" ? null : "__OTHER__");
  const toggleOtherMulti = (qid) => {
    setAnswers((a) => {
      const cur = Array.isArray(a[qid]) ? a[qid] : [];
      const has = cur.includes("__OTHER__");
      return { ...a, [qid]: has ? cur.filter((v) => v !== "__OTHER__") : [...cur, "__OTHER__"] };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Replace "__OTHER__" sentinel with "Other: <text>" before submitting.
    const payload = {};
    Object.entries(answers).forEach(([qid, val]) => {
      const ot = (otherText[qid] || "").trim();
      if (val === "__OTHER__") {
        if (!ot) return;
        payload[qid] = `Other: ${ot}`;
      } else if (Array.isArray(val)) {
        payload[qid] = val.map((v) => v === "__OTHER__" ? (ot ? `Other: ${ot}` : null) : v).filter(Boolean);
      } else if (val !== null && val !== undefined && val !== "") {
        payload[qid] = val;
      }
    });
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="poll-voting-form">
      {(poll.questions || []).map((q, qIdx) => (
        <div key={q.question_id} className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-4">
          <Label className="text-[#6B4E8A] font-medium">
            {qIdx + 1}. {q.text}
            {q.required && <span className="text-[#DC2626] ml-1">*</span>}
          </Label>
          {q.type === "single_choice" && (
            <div className="mt-3 space-y-2">
              {q.options.map((opt) => (
                <label
                  key={opt}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    answers[q.question_id] === opt
                      ? "border-[#6B4E8A] bg-[#6B4E8A]/5"
                      : "border-[#E5DFEC] hover:border-[#6B4E8A]/50"
                  }`}
                  data-testid={`option-${q.question_id}-${opt.replace(/\s+/g, "-")}`}
                >
                  <input
                    type="radio"
                    name={q.question_id}
                    checked={answers[q.question_id] === opt}
                    onChange={() => setSingle(q.question_id, opt)}
                    className="accent-[#6B4E8A]"
                  />
                  <span className="text-sm text-[#6B4E8A]">{opt}</span>
                </label>
              ))}
              {q.allow_other && (
                <div>
                  <label
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      answers[q.question_id] === "__OTHER__"
                        ? "border-[#6B4E8A] bg-[#6B4E8A]/5"
                        : "border-[#E5DFEC] hover:border-[#6B4E8A]/50"
                    }`}
                    data-testid={`option-${q.question_id}-other`}
                  >
                    <input
                      type="radio"
                      name={q.question_id}
                      checked={answers[q.question_id] === "__OTHER__"}
                      onChange={() => toggleOtherSingle(q.question_id)}
                      className="accent-[#6B4E8A]"
                    />
                    <span className="text-sm text-[#6B4E8A] italic">Other:</span>
                  </label>
                  {answers[q.question_id] === "__OTHER__" && (
                    <Input
                      value={otherText[q.question_id] || ""}
                      onChange={(e) => setOtherText({ ...otherText, [q.question_id]: e.target.value })}
                      placeholder="Tell us what you mean…"
                      maxLength={280}
                      className="mt-2 bg-white"
                      data-testid={`other-text-${q.question_id}`}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {q.type === "multi_choice" && (
            <div className="mt-3 space-y-2">
              {q.options.map((opt) => {
                const checked = Array.isArray(answers[q.question_id]) && answers[q.question_id].includes(opt);
                return (
                  <label
                    key={opt}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      checked ? "border-[#6B4E8A] bg-[#6B4E8A]/5" : "border-[#E5DFEC] hover:border-[#6B4E8A]/50"
                    }`}
                    data-testid={`option-${q.question_id}-${opt.replace(/\s+/g, "-")}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setMulti(q.question_id, opt)}
                      className="accent-[#6B4E8A]"
                    />
                    <span className="text-sm text-[#6B4E8A]">{opt}</span>
                  </label>
                );
              })}
              {q.allow_other && (() => {
                const checked = Array.isArray(answers[q.question_id]) && answers[q.question_id].includes("__OTHER__");
                return (
                  <div>
                    <label
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        checked ? "border-[#6B4E8A] bg-[#6B4E8A]/5" : "border-[#E5DFEC] hover:border-[#6B4E8A]/50"
                      }`}
                      data-testid={`option-${q.question_id}-other`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOtherMulti(q.question_id)}
                        className="accent-[#6B4E8A]"
                      />
                      <span className="text-sm text-[#6B4E8A] italic">Other:</span>
                    </label>
                    {checked && (
                      <Input
                        value={otherText[q.question_id] || ""}
                        onChange={(e) => setOtherText({ ...otherText, [q.question_id]: e.target.value })}
                        placeholder="Tell us what you mean…"
                        maxLength={280}
                        className="mt-2 bg-white"
                        data-testid={`other-text-${q.question_id}`}
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {q.type === "scale" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Array.from({ length: (q.scale_max || 5) - (q.scale_min || 1) + 1 }, (_, i) => (q.scale_min || 1) + i).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setSingle(q.question_id, n)}
                  className={`w-11 h-11 rounded-xl border font-semibold transition-colors ${
                    answers[q.question_id] === n
                      ? "border-[#6B4E8A] bg-[#6B4E8A] text-white"
                      : "border-[#E5DFEC] text-[#6B4E8A] hover:border-[#6B4E8A]"
                  }`}
                  data-testid={`scale-${q.question_id}-${n}`}
                >
                  {n}
                </button>
              ))}
              {(q.scale_min_label || q.scale_max_label) && (
                <div className="w-full flex justify-between text-xs text-[#94A3B8] mt-1">
                  <span>{q.scale_min_label || ""}</span>
                  <span>{q.scale_max_label || ""}</span>
                </div>
              )}
            </div>
          )}
          {q.type === "text" && (
            <Textarea
              value={answers[q.question_id] || ""}
              onChange={(e) => setSingle(q.question_id, e.target.value)}
              placeholder="Type your answer…"
              maxLength={800}
              className="mt-3 bg-white"
              data-testid={`text-${q.question_id}`}
            />
          )}
        </div>
      ))}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"
        data-testid="submit-vote-btn"
      >
        {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Vote size={16} className="mr-2" />}
        Submit anonymous response
      </Button>
      <p className="text-xs text-[#94A3B8] text-center">
        Your name and email are never linked to your answers.
      </p>
    </form>
  );
};

const PollCard = ({ poll, onSubmit, fetchResults, results, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const showResults = poll.viewer_voted || poll.results_visibility === "always" || poll.status === "closed";

  const handleVote = async (answers) => {
    setSubmitting(true);
    try {
      await onSubmit(poll, answers);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5DFEC] rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`poll-card-${poll.poll_id}`}
    >
      <div className="flex items-start">
      <button
        type="button"
        onClick={() => {
          const next = !expanded;
          setExpanded(next);
          if (next && showResults) fetchResults(poll);
        }}
        className="flex-1 min-w-0 px-4 sm:px-5 py-4 flex items-start gap-3 text-left"
      >
        <div className="w-10 h-10 rounded-xl bg-[#6B4E8A]/10 text-[#6B4E8A] flex items-center justify-center shrink-0">
          <Vote size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {poll.category && (
                <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#6B4E8A]/10 text-[#6B4E8A] mb-1">
                  {poll.category}
                </span>
              )}
              <h3 className="font-semibold text-[#6B4E8A] leading-snug break-words">{poll.title}</h3>
            </div>
            <ChevronDown
              size={18}
              className={`text-[#94A3B8] shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </div>
          {poll.description && (
            <p className="text-sm text-[#64748B] mt-1 leading-relaxed line-clamp-2">{poll.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            <span className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] rounded-full">
              {poll.questions?.length || 0} question{poll.questions?.length === 1 ? "" : "s"}
            </span>
            {poll.viewer_voted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#8DB5A0]/15 text-[#3F7C5C] rounded-full">
                <CheckCircle2 size={12} /> Submitted
              </span>
            )}
            {poll.status === "closed" && (
              <span className="px-2 py-0.5 bg-[#FAF7F2] text-[#94A3B8] rounded-full">Closed</span>
            )}
            <span className="text-[#94A3B8]">{poll.total_responses || 0} {(poll.total_responses || 0) === 1 ? "response" : "responses"}</span>
            <EditedLabel editedAt={poll.edited_at} testId={`poll-${poll.poll_id}-edited`} />
          </div>
        </div>
      </button>
      <div className="pt-3 pr-3">
          <ContentActionsMenu
            canManage={poll.can_manage}
            reportType="poll"
            reportId={poll.poll_id}
            testIdPrefix={`poll-${poll.poll_id}`}
            onEdit={poll.edit_locked ? undefined : () => onEdit(poll)}
            onDelete={() => onDelete(poll)}
            deleteTitle="Delete this poll?"
            deleteDescription="This permanently removes the poll and every response submitted to it. This cannot be undone."
          />
        </div>
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[#E5DFEC] overflow-hidden"
          >
            <div className="p-4 sm:p-5">
              {poll.status === "closed" && !poll.viewer_voted && (
                <div className="mb-4 bg-[#FAF7F2] border border-[#E5DFEC] rounded-lg p-3 text-sm text-[#64748B] inline-flex items-center gap-2">
                  <Lock size={14} /> This poll is closed. Results below.
                </div>
              )}
              {showResults
                ? results
                  ? <PollResults results={results} />
                  : (
                    <div className="text-center py-10">
                      <Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" />
                      <p className="text-xs text-[#94A3B8] mt-2">Loading results…</p>
                    </div>
                  )
                : poll.status === "active"
                  ? <PollVotingForm poll={poll} onSubmit={handleVote} submitting={submitting} />
                  : (
                    <div className="text-center py-10 text-sm text-[#94A3B8]">
                      <Lock size={20} className="mx-auto mb-2" />
                      Results are private to the DHDH team.
                    </div>
                  )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PollsPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("polls");
  const [polls, setPolls] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [pollResults, setPollResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestionForm, setSuggestionForm] = useState({ title: "", description: "" });
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const [showPollBuilder, setShowPollBuilder] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [editingSuggestionId, setEditingSuggestionId] = useState(null);
  const [overridePrompt, setOverridePrompt] = useState(null); // { message, resolve }

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return !!(user.is_admin || user.admin_role === "founder" || user.role === "founder");
  }, [user]);

  const isPaid = useMemo(() => {
    if (!user) return false;
    if (user.is_admin || user.admin_role === "founder" || user.role === "founder") return true;
    if (user.member_type === "guest_professional") return false;
    if (user.verification_status !== "verified") return false;
    return ["active", "trialing"].includes(user.membership_status);
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pollsRes, sugRes] = await Promise.allSettled([
        axios.get(`${API}/polls`),
        axios.get(`${API}/polls/suggestions`),
      ]);
      if (pollsRes.status === "fulfilled") setPolls(pollsRes.value.data);
      if (sugRes.status === "fulfilled") setSuggestions(sugRes.value.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPaid) {
      loadAll();
    } else {
      // setTimeout pushes the setState off the render cycle so it doesn't
      // trip react-hooks/set-state-in-effect.
      const t = setTimeout(() => setLoading(false), 0);
      return () => clearTimeout(t);
    }
  }, [isPaid]);

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestionForm.title.trim()) return;
    setSubmittingSuggestion(true);
    try {
      if (editingSuggestionId) {
        await axios.put(`${API}/polls/suggestions/${editingSuggestionId}`, suggestionForm);
        toast.success("Suggestion updated.");
      } else {
        await axios.post(`${API}/polls/suggestions`, suggestionForm);
        toast.success("Suggestion submitted! Members can now upvote it.");
      }
      setSuggestionDialog(false);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit suggestion.");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const handleUpvote = async (s) => {
    if (s.is_own) return;
    try {
      const res = await axios.post(`${API}/polls/suggestions/${s.suggestion_id}/upvote`);
      setSuggestions((prev) => prev.map((x) =>
        x.suggestion_id === s.suggestion_id
          ? { ...x, viewer_upvoted: res.data.viewer_upvoted, upvote_count: res.data.upvote_count }
          : x
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update vote.");
    }
  };

  const setSuggestionDialog = (open) => {
    setShowSuggestionDialog(open);
    if (!open) { setEditingSuggestionId(null); setSuggestionForm({ title: "", description: "" }); }
  };

  const startEditSuggestion = (s) => {
    setEditingSuggestionId(s.suggestion_id);
    setSuggestionForm({ title: s.title, description: s.description || "" });
    setShowSuggestionDialog(true);
  };

  const askOverride = (message) => new Promise((resolve) => setOverridePrompt({ message, resolve }));
  const resolveOverride = (ok) => { overridePrompt?.resolve(ok); setOverridePrompt(null); };

  const handleEditPoll = async (payload) => {
    const url = `${API}/polls/${editingPoll.poll_id}`;
    try {
      let res;
      try {
        res = await axios.put(url, payload);
      } catch (err) {
        const d = err.response?.data?.detail;
        if (err.response?.status !== 409 || d?.code !== "confirm_required") throw err;
        const ok = await askOverride(d.message);
        if (!ok) throw new Error("cancelled");
        res = await axios.put(url, { ...payload, confirm_override: true });
      }
      setPolls((prev) => prev.map((p) => p.poll_id === res.data.poll_id ? { ...p, ...res.data } : p));
      setPollResults((r) => { const n = { ...r }; delete n[res.data.poll_id]; return n; });
      toast.success("Poll updated.");
    } catch (err) {
      if (err.message !== "cancelled") {
        const d = err.response?.data?.detail;
        toast.error((typeof d === "string" ? d : d?.message) || "Couldn't update poll.");
      }
      throw err;
    }
  };

  const handleDeletePoll = async (poll) => {
    try {
      await axios.delete(`${API}/polls/${poll.poll_id}`);
      setPolls((prev) => prev.filter((p) => p.poll_id !== poll.poll_id));
      toast.success("Poll and its responses deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete poll.");
    }
  };

  const handleDeleteSuggestion = async (s) => {
    try {
      await axios.delete(`${API}/polls/suggestions/${s.suggestion_id}`);
      setSuggestions((prev) => prev.filter((x) => x.suggestion_id !== s.suggestion_id));
      toast.success("Suggestion removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove suggestion.");
    }
  };

  const fetchResults = async (poll) => {
    if (pollResults[poll.poll_id]) return;
    try {
      const res = await axios.get(`${API}/polls/${poll.poll_id}/results`);
      setPollResults((p) => ({ ...p, [poll.poll_id]: res.data }));
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Couldn't load results.");
      }
    }
  };

  const handleCreatePoll = async (payload) => {
    try {
      await axios.post(`${API}/admin/polls`, payload);
      toast.success(payload.status === "active" ? "Poll published — members can vote now." : "Saved as draft.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create poll.");
      throw err;
    }
  };

  const handleVote = async (poll, answers) => {
    try {
      await axios.post(`${API}/polls/${poll.poll_id}/vote`, { answers });
      toast.success("Thank you — your response was recorded anonymously.");
      // Reload the specific poll's data + results
      const fresh = await axios.get(`${API}/polls/${poll.poll_id}`);
      setPolls((prev) => prev.map((p) => p.poll_id === poll.poll_id ? fresh.data : p));
      const res = await axios.get(`${API}/polls/${poll.poll_id}/results`);
      setPollResults((rp) => ({ ...rp, [poll.poll_id]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit vote.");
      throw err;
    }
  };

  if (!isPaid) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Vote className="mx-auto text-[#94A3B8]" size={36} />
          <h2 className="text-xl font-semibold text-[#6B4E8A] mt-4">Polls are for paid members</h2>
          <p className="text-sm text-[#64748B] mt-2">
            Pick a membership tier to suggest topics and shape what we build next.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#8DB5A0] font-semibold">
              <Sparkles size={14} /> Members&apos; Voice
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-[#6B4E8A] mt-1.5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Polls & Surveys
            </h1>
            <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
              Shape what we build next. Suggest a topic, upvote what matters to you, and weigh in on official polls.
              Your responses are completely anonymous.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Button
                onClick={() => setShowPollBuilder(true)}
                className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"
                data-testid="create-new-poll-btn"
              >
                <Vote size={16} className="mr-2" /> Create new poll
              </Button>
            )}
            <Button
              onClick={() => setShowSuggestionDialog(true)}
              variant={isAdmin ? "outline" : "default"}
              className={isAdmin
                ? "border-[#6B4E8A] text-[#6B4E8A] hover:bg-[#6B4E8A] hover:text-white rounded-full"
                : "bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"}
              data-testid="open-suggestion-dialog-btn"
            >
              <MessageSquarePlus size={16} className="mr-2" /> Suggest a topic
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-5">
          <TabsList className="bg-[#F0ECF7] p-1.5 rounded-xl">
            <TabsTrigger value="polls" className="data-[state=active]:bg-white px-4 py-1.5 rounded-lg" data-testid="tab-polls">
              Active polls ({polls.filter((p) => p.status === "active").length})
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="data-[state=active]:bg-white px-4 py-1.5 rounded-lg" data-testid="tab-suggestions">
              Suggestion board ({suggestions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="polls" className="space-y-3">
            {loading ? (
              <div className="text-center py-16">
                <Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" />
              </div>
            ) : polls.length === 0 ? (
              <div className="text-center py-16 bg-white border border-dashed border-[#E5DFEC] rounded-2xl">
                <Vote size={28} className="mx-auto text-[#94A3B8]" />
                <p className="text-sm text-[#64748B] mt-3">No polls right now. Head to the Suggestion board to start the conversation.</p>
              </div>
            ) : (
              polls.map((p) => (
                <PollCard
                  key={p.poll_id}
                  poll={p}
                  onSubmit={handleVote}
                  fetchResults={fetchResults}
                  results={pollResults[p.poll_id]}
                  onEdit={setEditingPoll}
                  onDelete={handleDeletePoll}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="suggestions" className="space-y-3">
            {loading ? (
              <div className="text-center py-16">
                <Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-16 bg-white border border-dashed border-[#E5DFEC] rounded-2xl">
                <MessageSquarePlus size={28} className="mx-auto text-[#94A3B8]" />
                <p className="text-sm text-[#64748B] mt-3">Be the first to suggest a topic the team should poll the community on.</p>
              </div>
            ) : (
              <AnimatePresence>
                {suggestions.map((s) => (
                  <SuggestionCard
                    key={s.suggestion_id}
                    s={s}
                    onUpvote={handleUpvote}
                    onEdit={startEditSuggestion}
                    onDelete={handleDeleteSuggestion}
                  />
                ))}
              </AnimatePresence>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Suggest topic dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setSuggestionDialog}>
        <DialogContent data-testid="suggestion-dialog">
          <DialogHeader>
            <DialogTitle>{editingSuggestionId ? "Edit suggestion" : "Suggest a poll topic"}</DialogTitle>
            <DialogDescription>
              Other members will see your idea and upvote it. The team reviews the leaderboard weekly to decide what to launch as an official poll.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSuggestion} className="space-y-4">
            <div>
              <Label htmlFor="sug-title">Title</Label>
              <Input
                id="sug-title"
                value={suggestionForm.title}
                onChange={(e) => setSuggestionForm({ ...suggestionForm, title: e.target.value })}
                placeholder="What should we ask the community?"
                maxLength={140}
                required
                data-testid="suggestion-title-input"
              />
            </div>
            <div>
              <Label htmlFor="sug-desc">Why it matters (optional)</Label>
              <Textarea
                id="sug-desc"
                value={suggestionForm.description}
                onChange={(e) => setSuggestionForm({ ...suggestionForm, description: e.target.value })}
                placeholder="A sentence or two of context for fellow members."
                maxLength={600}
                rows={3}
                data-testid="suggestion-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSuggestionDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submittingSuggestion} className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white" data-testid="submit-suggestion-btn">
                {submittingSuggestion ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                {editingSuggestionId ? "Save changes" : "Submit suggestion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Admin-only poll builder */}
      {showPollBuilder && isAdmin && (
        <PollBuilder
          initial={null}
          suggestion={null}
          onClose={() => setShowPollBuilder(false)}
          onSave={handleCreatePoll}
        />
      )}
      {editingPoll && (
        <PollBuilder
          key={editingPoll.poll_id}
          initial={editingPoll}
          suggestion={null}
          onClose={() => setEditingPoll(null)}
          onSave={handleEditPoll}
        />
      )}
      <AlertDialog open={!!overridePrompt} onOpenChange={(o) => !o && resolveOverride(false)}>
        <AlertDialogContent data-testid="poll-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#B45309]">
              <AlertTriangle size={18} /> Founder override
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="poll-override-message">{overridePrompt?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolveOverride(false)} data-testid="poll-override-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => resolveOverride(true)} className="bg-[#B45309] hover:bg-[#92400E] text-white" data-testid="poll-override-confirm">
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default PollsPage;
